import { TrashIcon } from "@heroicons/react/24/solid";
import { Statuses } from "../../constants/statuses";
import { useDispatch } from "react-redux";
import { setSelectedCase } from "../../redux/casesRedux";
import { Roles } from "../../constants/roles";
import { anatomyConstants } from "../../constants/anatomyConstants";

const statusColor = (status) => {
  if (status === Statuses.READY) return "#4ADE80";
  if (status === Statuses.IN_PROGRESS) return "#FBBF24";
  if (status === Statuses.UPLOAD_DICOM) return "#FB923C";
  if (status === Statuses.DEMO) return "#60A5FA";
  return "#94A3B8";
};

const CaseTable = ({ caseList, showDeleteButtons, openHardDeleteModal, openSoftDeleteModal, navigate, user }) => {
  const dispatch = useDispatch();

  const isCreator = (caseItem) =>
    Object.entries(caseItem.users || {}).some(
      ([userId, userInfo]) => userId === user?.id && userInfo.role === Roles.CREATOR
    );

  const getSharedUsers = (caseItem) =>
    Object.values(caseItem.users || {})
      .filter((u) => u.role !== Roles.CREATOR)
      .map((u) => u.name)
      .join(", ") || "None";

  const formatExpiration = (exp) => {
    if (!exp || exp === "Invalid Date") return "";
    return exp;
  };

  return (
    <div className="grid grid-cols-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
      {caseList.map((caseItem) => (
        <div
          key={caseItem.id}
          className="relative rounded-xl transition-all flex flex-col cursor-pointer"
          style={{
            minHeight: 405,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "rgba(48,166,156,0.4)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1), 0 0 0 1px rgba(48,166,156,0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#E2E8F0";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
          }}
          onClick={() => {
            dispatch(setSelectedCase(caseItem));
            navigate(`/case/${caseItem.id}`);
          }}
        >
          {/* Thumbnail */}
          <div
            className="w-full h-44 flex items-center justify-center rounded-t-xl overflow-hidden"
            style={{ backgroundColor: "#F1F5F9" }}
          >
            <img
              src={caseItem.previewUrl || anatomyConstants.mainImage}
              alt={caseItem.title}
              className="w-full h-full object-contain"
              onError={(e) => { e.target.onerror = null; e.target.src = anatomyConstants.mainImage; }}
            />
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col p-4 space-y-1">
            <div className="flex-1 space-y-1">
              <div className="font-semibold text-base truncate" style={{ color: "#0F172A" }}>
                {caseItem.title}
              </div>
              {[
                { label: "Anatomy", val: caseItem.anatomy },
                { label: "Creator", val: caseItem.creator },
                { label: "Requester", val: caseItem.requester },
                { label: "Shared", val: !caseItem.isDemo ? getSharedUsers(caseItem) : "" },
                { label: "Created", val: caseItem.creationDate },
                { label: "Due", val: caseItem.dueDate },
                { label: "Expires", val: formatExpiration(caseItem.expirationDate) },
              ].map(({ label, val }) => (
                <div key={label} className="text-xs" style={{ color: "#94A3B8" }}>
                  {label}:{" "}
                  <span style={{ color: "#475569" }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Status */}
            <div
              className="flex items-center justify-between pt-2 mt-2"
              style={{ borderTop: "1px solid #F1F5F9" }}
            >
              <span className="text-xs font-medium" style={{ color: "#94A3B8" }}>Status</span>
              <span className="text-xs font-bold" style={{ color: statusColor(caseItem.status) }}>
                {caseItem.status}
              </span>
            </div>

            {/* Admin badge */}
            {caseItem.isAdminView && (
              <div className="absolute top-2 left-2">
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: "rgba(139,92,246,0.2)", color: "#A78BFA", border: "1px solid rgba(139,92,246,0.3)" }}
                >
                  Admin View
                </span>
              </div>
            )}

            {/* Delete button */}
            {showDeleteButtons && (
              <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCreator(caseItem)) openHardDeleteModal(caseItem.id);
                    else openSoftDeleteModal(caseItem.id);
                  }}
                  className="p-2 rounded-full transition"
                  style={{
                    backgroundColor: isCreator(caseItem) ? "rgba(239,68,68,0.8)" : "rgba(34,197,94,0.8)",
                    color: "#fff",
                  }}
                  title={isCreator(caseItem) ? "Delete for everyone" : "Remove from my list"}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
      {caseList.length === 0 && (
        <div className="col-span-full text-center py-12" style={{ color: "#64748B" }}>
          No cases found.
        </div>
      )}
    </div>
  );
};

export default CaseTable;
